import { padStart } from 'lodash';
import { formatToNumber } from 'utils/lodash';
import z from 'zod';

interface ITimezoneOffset {
  showTimeStandard?: boolean;
  showTimeZone?: boolean;
}

/**
 * @returns timezone offset in the format of "+5:45" or "GMT+5:45"
 */
export const getTimezoneOffset = ({
  showTimeStandard = false,
  showTimeZone = false,
}: ITimezoneOffset = {}) => {
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const parts = new Intl.DateTimeFormat('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    timeZoneName: 'shortOffset',
  }).formatToParts(new Date());

  const offset = parts.find((part) => part.type === 'timeZoneName')?.value; // e.g., "GMT+5:45"

  if (showTimeStandard) {
    return offset; // e.g., "GMT+5:45"
  }

  if (showTimeZone) {
    return `${offset}|${timeZone}`.replace('GMT', ''); //   e.g. "+5:45|Asia/Katmandu"
  }

  return offset?.replace('GMT', '') ?? ''; // e.g., "+5:45" or "-5:45"
};

export const getFormattedTime = (date: Date) => {
  const hours = String(date.getUTCHours()).padStart(2, '0');
  const minutes = String(date.getUTCMinutes()).padStart(2, '0');
  return `${hours}:${minutes}:00Z`;
};

/**
 * @returns the local timezone of the user FORMAT: "Asia/Katmandu"
 */
export const getLocalTimeZone = () => {
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return timezone;
};

// Zod schemas for validation
const HoursSchema = z.number().min(0);
const DayHoursSchema = z.number().min(0).max(24);
const MinutesSchema = z.preprocess((value) => {
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return value;
  const rounded = Math.round(n);
  return Math.min(59, Math.max(0, rounded));
}, z.number().min(0).max(59));

const parseWithFallback = <T>(
  schema: z.ZodType<T>,
  value: number,
  fallback: T,
): T => {
  const result = schema.safeParse(value);
  return result.success ? result.data : fallback;
};

export const DURATION_TYPES = {
  DAY: 'day',
  ALL: 'all',
} as const;
type IDurationType = (typeof DURATION_TYPES)[keyof typeof DURATION_TYPES];

/**
 * Formats hours and minutes back to duration string
 * @param hrs - Hours as string or number
 * @param mins - Minutes as string or number
 * @returns Formatted duration string like "9h 30m"
 * @example
 * formatDurationString(9, 30) // returns "9h 30m"
 * formatDurationString('12', '5') // returns "12h 05m"
 */
export const formatDurationString = ({
  hrs,
  mins,
  type = DURATION_TYPES.ALL,
}: {
  hrs: string | number;
  mins: string | number;
  type?: IDurationType;
}): string => {
  const hoursNum = formatToNumber(hrs);
  const minsNum = formatToNumber(mins);

  const validHours = parseWithFallback(
    type === 'day' ? DayHoursSchema : HoursSchema,
    hoursNum,
    0,
  );
  const validMins = parseWithFallback(MinutesSchema, minsNum, 0);

  return `${validHours}h ${padStart(validMins.toString(), 2, '0')}m`;
};

/**
 * Converts total minutes to duration string
 * @param totalMinutes - Total minutes as number
 * @returns Formatted duration string
 * @example
 * formatMinutesToDurationString(150, 'day') // returns "2h 30m"
 * formatMinutesToDurationString(1500, 'all') // returns "25h 00m"
 */
export const formatMinutesToDurationString = (
  totalMinutes: number,
  type?: IDurationType,
): string => {
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return formatDurationString({ hrs: hours, mins: minutes, type });
};
